import { useMutation } from '@tanstack/react-query';
import { exportCsvInformations } from '@/services/admin/informations';
import type { InformationListParams } from '@/services/admin/informations';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants/messages';

const downloadInformationsCsvFn = async (
  site: string,
  params: Omit<InformationListParams, 'page' | 'per_page'>
): Promise<void> => {
  const blob = await exportCsvInformations(site, params);
  const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=UTF-8;' }));

  const a = document.createElement('a');
  a.href = url;
  a.download = `informations${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Hook to download admin informations CSV (API + blob download). Handles success/error toast.
 */
export const useDownloadInformationsCsv = () => {
  const toast = useAppToast();
  const mutation = useMutation({
    mutationFn: ({
      site,
      params,
    }: {
      site: string;
      params: Omit<InformationListParams, 'page' | 'per_page'>;
    }) => downloadInformationsCsvFn(site, params),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, CSV_ERROR_MESSAGE));
    },
  });

  const downloadCsv = (site: string, params: Omit<InformationListParams, 'page' | 'per_page'>) => {
    mutation.mutate({ site, params });
  };

  return { downloadCsv, isDownloading: mutation.isPending };
};
